import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import vfs from '../../lib/virtualFS';
import { Play, Loader2, Plus, Trash2, Terminal } from 'lucide-react';

function CodeCell({ id, initialCode, onRun, running, onCodeChange }) {
  const [code, setCode] = useState(initialCode || '');
  const [output, setOutput] = useState(null);
  const [error, setError] = useState(null);

  // Sync external code changes (from chat sidebar)
  useEffect(() => {
    if (initialCode !== undefined && initialCode !== null) {
      setCode(initialCode);
    }
  }, [initialCode]);

  const handleRun = async () => {
    setOutput(null);
    setError(null);
    const result = await onRun(code);
    if (result.error) setError(result.error);
    if (result.output) setOutput(result.output);
  };

  return (
    <div className="group border border-white/[0.06] rounded-lg bg-[#0a0a0a] hover:border-white/10 transition-colors" data-testid={`code-cell-${id}`}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-neutral-600">In [{id}]</span>
        </div>
        <button
          onClick={handleRun}
          disabled={running}
          className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
          data-testid={`run-cell-${id}`}
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" strokeWidth={2} />}
          Run
        </button>
      </div>

      <div className="relative">
        <textarea
          value={code}
          onChange={(e) => { setCode(e.target.value); if (onCodeChange) onCodeChange(e.target.value); }}
          className="w-full bg-transparent text-sm text-green-300 font-mono p-4 resize-none focus:outline-none min-h-[60px] leading-relaxed"
          spellCheck={false}
          style={{ tabSize: 4 }}
          onKeyDown={(e) => {
            if (e.key === 'Tab') {
              e.preventDefault();
              const start = e.target.selectionStart;
              const end = e.target.selectionEnd;
              setCode(code.substring(0, start) + '    ' + code.substring(end));
              setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = start + 4; }, 0);
            }
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              handleRun();
            }
          }}
          data-testid={`cell-editor-${id}`}
        />
      </div>

      {(output || error) && (
        <div className="border-t border-white/[0.04] px-4 py-3">
          <span className="text-[10px] font-mono text-neutral-600 mb-1 block">Out [{id}]</span>
          {output && <pre className="text-xs font-mono text-neutral-300 whitespace-pre-wrap">{output}</pre>}
          {error && <pre className="text-xs font-mono text-red-400 whitespace-pre-wrap">{error}</pre>}
        </div>
      )}
    </div>
  );
}

const NotebookPane = forwardRef(function NotebookPane({ runPython, pyodideReady, pyodideLoading, activeFile, onFileSelect }, ref) {
  const [cells, setCells] = useState([{ id: 1, code: '' }]);
  const [nextId, setNextId] = useState(2);
  const [runningCell, setRunningCell] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [mode, setMode] = useState('notebook');
  const [fileOutput, setFileOutput] = useState(null);
  const [fileVersion, setFileVersion] = useState(0);

  // Load file content when activeFile changes or VFS updates
  useEffect(() => {
    if (activeFile) {
      const content = vfs.readFile(activeFile);
      if (content !== null) setEditorContent(content);
    }
  }, [activeFile, fileVersion]);

  // Listen for VFS changes to refresh editor
  useEffect(() => {
    return vfs.subscribe(() => setFileVersion(v => v + 1));
  }, []);

  // Expose actions to parent (WorkspacePage → ChatSidebar)
  useImperativeHandle(ref, () => ({
    addCellWithCode: (code) => {
      const newId = nextId;
      setCells(prev => [...prev, { id: newId, code }]);
      setNextId(prev => prev + 1);
      setMode('notebook');
      return newId;
    },
    updateCellCode: (cellId, code) => {
      setCells(prev => prev.map(c => c.id === cellId ? { ...c, code } : c));
    },
    switchToFile: (filename) => {
      onFileSelect(filename);
      setMode('editor');
    },
    switchToNotebook: () => {
      setMode('notebook');
    },
    refreshEditor: () => {
      setFileVersion(v => v + 1);
    },
  }), [nextId, onFileSelect]);

  const handleRunCell = useCallback(async (code) => {
    if (!pyodideReady) return { output: '', error: 'Pyodide is loading...' };
    return await runPython(code);
  }, [runPython, pyodideReady]);

  const runCellById = useCallback(async (id, code) => {
    setRunningCell(id);
    const result = await handleRunCell(code);
    setRunningCell(null);
    return result;
  }, [handleRunCell]);

  const addCell = () => {
    setCells(prev => [...prev, { id: nextId, code: '' }]);
    setNextId(prev => prev + 1);
  };

  const removeCell = (id) => {
    setCells(prev => prev.filter(c => c.id !== id));
  };

  const saveFile = () => {
    if (activeFile) {
      vfs.writeFile(activeFile, editorContent);
    }
  };

  const runFile = async () => {
    if (!pyodideReady || !activeFile) return;
    setFileOutput(null);
    const result = await runPython(editorContent);
    setFileOutput(result);
  };

  return (
    <div className="h-full flex flex-col bg-[#0c0c0c]" data-testid="notebook-pane">
      {/* Tab bar */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-white/[0.06] bg-[#0a0a0a]">
        <button
          onClick={() => setMode('notebook')}
          className={`px-3 py-1 text-xs rounded transition-colors ${
            mode === 'notebook' ? 'bg-white/10 text-white' : 'text-neutral-500 hover:text-neutral-300'
          }`}
          data-testid="tab-notebook"
        >
          <Terminal className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={1.5} />
          Notebook
        </button>
        {activeFile && (
          <button
            onClick={() => setMode('editor')}
            className={`px-3 py-1 text-xs rounded transition-colors font-mono ${
              mode === 'editor' ? 'bg-white/10 text-white' : 'text-neutral-500 hover:text-neutral-300'
            }`}
            data-testid="tab-editor"
          >
            {activeFile}
          </button>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 text-[10px]">
          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
            pyodideReady
              ? 'bg-green-500/10 text-green-400'
              : 'bg-yellow-500/10 text-yellow-400'
          }`}>
            {pyodideReady ? (
              <><span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Python Ready</>
            ) : (
              <><Loader2 className="w-3 h-3 animate-spin" /> Loading Pyodide...</>
            )}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {mode === 'notebook' ? (
          <>
            {cells.map((cell) => (
              <div key={cell.id} className="relative group">
                <CodeCell
                  id={cell.id}
                  initialCode={cell.code}
                  onRun={(code) => runCellById(cell.id, code)}
                  running={runningCell === cell.id}
                />
                {cells.length > 1 && (
                  <button
                    onClick={() => removeCell(cell.id)}
                    className="absolute -right-1 -top-1 opacity-0 group-hover:opacity-100 w-5 h-5 rounded bg-red-500/20 text-red-400 flex items-center justify-center transition-opacity"
                    data-testid={`remove-cell-${cell.id}`}
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addCell}
              className="w-full py-2 border border-dashed border-white/[0.08] rounded-lg text-xs text-neutral-500 hover:text-neutral-300 hover:border-white/15 transition-colors flex items-center justify-center gap-1.5"
              data-testid="add-cell-button"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add Cell
            </button>
          </>
        ) : (
          <>
            {/* File editor mode */}
            <div className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] overflow-hidden">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.04]">
                <span className="text-xs font-mono text-neutral-400">{activeFile}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveFile}
                    className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
                    data-testid="save-file-button"
                  >
                    Save
                  </button>
                  <button
                    onClick={runFile}
                    disabled={!pyodideReady}
                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                    data-testid="run-file-button"
                  >
                    <Play className="w-3 h-3" strokeWidth={2} /> Run
                  </button>
                </div>
              </div>
              <textarea
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                className="w-full bg-transparent text-sm text-green-300 font-mono p-4 resize-none focus:outline-none min-h-[400px] leading-relaxed"
                spellCheck={false}
                style={{ tabSize: 4 }}
                onKeyDown={(e) => {
                  if (e.key === 'Tab') {
                    e.preventDefault();
                    const start = e.target.selectionStart;
                    const end = e.target.selectionEnd;
                    const newContent = editorContent.substring(0, start) + '    ' + editorContent.substring(end);
                    setEditorContent(newContent);
                    setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = start + 4; }, 0);
                  }
                  if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    saveFile();
                  }
                }}
                data-testid="file-editor"
              />
            </div>
            {fileOutput && (
              <div className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-4">
                <span className="text-[10px] font-mono text-neutral-600 mb-2 block">Output</span>
                {fileOutput.output && <pre className="text-xs font-mono text-neutral-300 whitespace-pre-wrap">{fileOutput.output}</pre>}
                {fileOutput.error && <pre className="text-xs font-mono text-red-400 whitespace-pre-wrap">{fileOutput.error}</pre>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

export default NotebookPane;
